import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayerGameScoreDialog } from '@sorare/gameplay/src/components/stats/PlayerGameScoreDialog';

import { CustomLineup } from 'components/rivals/matches/arena/ArenaLive/types';

import { Appearance } from './Appearance';
import {
  Lineup_footballRivalsLineup,
  Lineup_pollingFootballRivalsLineup,
} from './__generated__/index.graphql';

const LineupWrapper = styled(Horizontal)`
  justify-content: center;
  > * {
    flex: 1;
  }
  @media ${tabletAndAbove} {
    &:not(.compact) {
      gap: var(--double-unit);
    }
  }
`;

type Props = {
  lineup: CustomLineup<Lineup_footballRivalsLineup> | null;
  compact?: boolean;
  rarityBonusEnabled?: boolean;
  withGameScoreDialog?: boolean;
  showStartingStatus?: boolean;
  showPendingStatus?: boolean;
  showUpsell?: boolean;
  showSubButton?: boolean;
};
export const Lineup = ({
  lineup,
  compact,
  rarityBonusEnabled,
  withGameScoreDialog,
  showStartingStatus,
  showPendingStatus,
  showUpsell = true,
}: Props) => {
  const [displayedAppearance, setDisplayedAppearance] = useState<
    Lineup_footballRivalsLineup['appearances'][number] | undefined
  >();

  if (!lineup) {
    return null;
  }

  return (
    <>
      <LineupWrapper className={classNames({ compact })}>
        {lineup.appearances.map(appearance => {
          const props = {
            appearance,
            compact,
            rarityBonusEnabled,
            showStartingStatus,
            showPendingStatus,
            simulation: true,
            scoreNode:
              typeof appearance.statValue === 'number' ? (
                <Horizontal>
                  <img
                    src={lineup.tactic.iconUrl}
                    width={24}
                    height={24}
                    alt=""
                  />
                  <LabelM bold>
                    <AnimatedNumber value={appearance.statValue} />
                  </LabelM>
                </Horizontal>
              ) : null,
          };
          if (withGameScoreDialog && !!appearance.so5Score) {
            return (
              <LinkOther
                key={appearance.id}
                as="button"
                type="button"
                onClick={() => setDisplayedAppearance(appearance)}
              >
                <Appearance {...props} />
              </LinkOther>
            );
          }

          return <Appearance key={appearance.id} {...props} />;
        })}
      </LineupWrapper>
      {displayedAppearance?.so5Score && (
        <PlayerGameScoreDialog
          playerGameScoreId={displayedAppearance.so5Score.id}
          onClose={() => setDisplayedAppearance(undefined)}
          open={!!displayedAppearance}
          showUpsell={showUpsell && displayedAppearance.rarity === 'common'}
        />
      )}
    </>
  );
};

Lineup.pollingFragments = {
  footballRivalsLineup: gql`
    fragment Lineup_pollingFootballRivalsLineup on FootballRivalsLineup {
      id
      appearances {
        id
        ...Appearance_pollingFootballRivalsAppearance
      }
    }

    ${Appearance.pollingFragments.footballRivalsAppearance}
  ` as TypedDocumentNode<Lineup_pollingFootballRivalsLineup>,
};

Lineup.fragments = {
  footballRivalsLineup: gql`
    fragment Lineup_footballRivalsLineup on FootballRivalsLineup {
      id
      tactic {
        slug
        iconUrl
      }
      appearances {
        id
        rarity
        so5Score {
          id
        }
        ...Appearance_footballRivalsAppearance
      }
      ...Lineup_pollingFootballRivalsLineup
    }
    ${Appearance.fragments.footballRivalsAppearance}
    ${Lineup.pollingFragments.footballRivalsLineup}
  ` as TypedDocumentNode<Lineup_footballRivalsLineup>,
};
